import type { RepairDetails } from '@/lib/service/ecs/contracts';

export type RepairTimelineAction = RepairDetails['actionsHistory'][number];

/** Ordered repair milestones for the repair details status stepper (see ADR-0008). */
export function buildRepairStatusTimeline(details: RepairDetails): RepairTimelineAction[] {
  const actionsHistory = details.actionsHistory;
  const modeOfTransport = details.modeOfTransport;
  const pickupBoutiqueTracking = details.pickupBoutiqueTracking;
  const hasEstimateSent = actionsHistory.some((history) => history.actionCode === 'estimate_sent');
  const estimateAcceptedAction = actionsHistory.find(
    (action) => action.actionCode === 'estimate_accepted'
  );
  const estimateRefusedAction = actionsHistory.find(
    (action) => action.actionCode === 'estimate_refused'
  );
  const waitingPaymentAction = actionsHistory.find(
    (action) => action.actionCode === 'waiting_payment'
  );
  const paymentDoneAction = actionsHistory.find((action) => action.actionCode === 'payment_done');
  const paymentRefusedAction = actionsHistory.find(
    (action) => action.actionCode === 'payment_refused'
  );
  const preinvoiceDoneAction = actionsHistory.find(
    (action) => action.actionCode === 'preinvoice_done'
  );
  const actions: RepairTimelineAction[] = [{ actionCode: 'request_created' }];

  if (modeOfTransport === 'BreitlingSending') {
    actions.push({ actionCode: 'box_sent' });
  }
  if (modeOfTransport !== 'BoutiqueDropoff') {
    actions.push({ actionCode: 'watch_received' });
  }

  if (actionsHistory.some((action) => action.actionCode === 'analysis')) {
    actions.push({ actionCode: 'analysis' });
  }

  if (hasEstimateSent) {
    actions.push({ actionCode: 'estimate_sent' });
  }

  if (estimateAcceptedAction) {
    actions.push(estimateAcceptedAction);
  } else if (estimateRefusedAction) {
    actions.push(estimateRefusedAction);
  }

  actions.push({ actionCode: 'workshop' });
  if (waitingPaymentAction) {
    actions.push(waitingPaymentAction);
  }

  const waitingPaymentInProgress = actionsHistory.some(
    (history) => history.actionCode === 'waiting_payment' && history.status === 'In progress'
  );
  if (!waitingPaymentInProgress) {
    if (paymentDoneAction) {
      actions.push(paymentDoneAction);
    } else if (paymentRefusedAction) {
      actions.push(paymentRefusedAction);
    }
  }

  if (preinvoiceDoneAction) {
    actions.push(preinvoiceDoneAction);
  }

  const shippingTodoAction = actionsHistory.find((action) => action.actionCode === 'shipping_todo');
  if (shippingTodoAction) {
    actions.push(shippingTodoAction);
  }

  actions.push({ actionCode: 'shipping_done' });
  if (pickupBoutiqueTracking) {
    actions.push({ actionCode: 'pickup_ready' });
  }
  actions.push({ actionCode: 'completed' });

  actionsHistory.forEach((action) => {
    const idx = actions.findIndex((item) => item.actionCode === action.actionCode);
    if (idx !== -1) {
      actions[idx] = action;
    }
  });

  return actions;
}

/** Active step index for the status stepper (legacy: last completed + 1, minimum 1). */
export function getRepairTimelineActiveStepIndex(timeline: RepairTimelineAction[]): number {
  const lastCompletedIndex = timeline.findLastIndex((step) => step.status === 'Completed');
  return Math.max(lastCompletedIndex + 1, 1);
}
